import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const SEARCH_ICON = <span className="multi-value__search-icon" aria-hidden="true"><i className="fa fa-search" /></span>;
const CHIP_GAP = 4; // px, keep in sync with .multi-value__chips gap in templates/*/styles/blocks/multi-value.less

/**
 * Collapsed one-line view of a multi-value field (Jira-like):
 * as many values as fit in one line, then "… +N". Click (or tap) opens the editor.
 * Styles: templates/<template>/styles/blocks/multi-value.less
 */
const MultiValueView = ({ values = [], placeholder = '', editLabel = 'Edit', onEdit }) => {
    const chipsRef = useRef(null);
    const measureRef = useRef(null);
    const moreMeasureRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(values.length);

    const recalculate = useCallback(() => {
        const container = chipsRef.current;
        const measure = measureRef.current;
        if (!container || !measure) {
            return;
        }

        const available = container.clientWidth;
        const widths = Array.from(measure.children).map(el => el.getBoundingClientRect().width);
        const total = widths.reduce((sum, w, i) => sum + w + (i ? CHIP_GAP : 0), 0);

        if (total <= available) {
            setVisibleCount(widths.length);
            return;
        }

        // not everything fits: leave room for the "… +N" counter
        const moreWidth = (moreMeasureRef.current ? moreMeasureRef.current.getBoundingClientRect().width : 40) + CHIP_GAP;
        let used = 0;
        let count = 0;
        for (let i = 0; i < widths.length; i++) {
            const next = used + (i ? CHIP_GAP : 0) + widths[i];
            if (next + moreWidth > available) {
                break;
            }
            used = next;
            count++;
        }

        // always show at least one value (it is truncated with an ellipsis if it is too long)
        setVisibleCount(Math.max(1, count));
    }, []);

    useLayoutEffect(() => {
        recalculate();
    }, [values, recalculate]);

    useLayoutEffect(() => {
        if (typeof ResizeObserver === 'undefined' || !chipsRef.current) {
            return undefined;
        }
        const observer = new ResizeObserver(() => recalculate());
        observer.observe(chipsRef.current);
        return () => observer.disconnect();
    }, [recalculate]);

    const hiddenCount = Math.max(0, values.length - visibleCount);
    const titles = values.map(item => String(item && item.title !== undefined ? item.title : item));

    return (
        <button
            type="button"
            className="multi-value__view"
            onClick={onEdit}
            title={titles.join(', ')}
            aria-label={`${editLabel}: ${titles.join(', ') || placeholder}`}
        >
            <span className="multi-value__chips" ref={chipsRef}>
                {titles.length
                    ? titles.slice(0, visibleCount).map((title, i) => (
                        <span key={`${title}-${i}`} className="rw-multiselect-tag multi-value__chip"><span className="multi-value__chip-text">{title}</span></span>
                    ))
                    : <span className="multi-value__placeholder">{placeholder}</span>
                }
                {hiddenCount > 0 ? <span className="multi-value__more">… +{hiddenCount}</span> : null}
            </span>

            {/* invisible copies used only to measure widths */}
            <span className="multi-value__measure" ref={measureRef} aria-hidden="true">
                {titles.map((title, i) => (
                    <span key={`${title}-${i}`} className="rw-multiselect-tag multi-value__chip"><span className="multi-value__chip-text">{title}</span></span>
                ))}
            </span>
            <span className="multi-value__measure" aria-hidden="true">
                <span className="multi-value__more" ref={moreMeasureRef}>… +{Math.max(values.length, 9)}</span>
            </span>

            {/* empty field: the usual search icon instead of the pencil */}
            {!titles.length ? SEARCH_ICON : null}
            <span className={`multi-value__edit-icon${titles.length ? '' : ' multi-value__edit-icon--hidden'}`} aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325"/>
                </svg>
            </span>
        </button>
    );
};

MultiValueView.propTypes = {
    values: PropTypes.array,
    placeholder: PropTypes.string,
    editLabel: PropTypes.string,
    onEdit: PropTypes.func
};

export default MultiValueView;
